import type { JSONContent } from "@tiptap/core";
import { layout } from "./table";

// A book table as the editor holds it: caption, sections of rows, rows of cells, cells of inline content.
// Attributes stay an ordered list of pairs so the HTML comes back in the order it was written.
// The HTML a table has is only ever written one way (table.ts), so a table the model cannot write back
// as exactly that HTML is not a model at all: `tableNode` answers null and the table stays a locked block.

export type Pair = [string, string];
export const SECTIONS = ["thead", "tbody", "tfoot"];
// Outermost first. A mark order the source does not follow is not a table the model can hold.
const MARK_ORDER = ["link", "bold", "italic", "span"];
const ATTRIBUTES = {
	table: ["class"],
	tr: ["class", "style"],
	cell: ["class", "style", "width", "scope", "title"],
};
export type Kind = keyof typeof ATTRIBUTES;

// Table markup comes from a seed, the source view or the clipboard, and its attributes reach the editor's DOM and the
// chapter's HTML. Only these names pass, in a list of string pairs, and a style carries no way to load or run anything.
const UNSAFE_STYLE = /url\s*\(|javascript:|expression\s*\(|@import|behavior\s*:|-moz-binding|\\|\/\*/i;

export function validPairs(value: unknown, kind: Kind, extra: string[] = []): Pair[] | null {
	if (!Array.isArray(value) || value.length > 20) return null;
	const allowed = [...ATTRIBUTES[kind], ...extra];
	const seen = new Set<string>();
	for (const pair of value) {
		if (!Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== "string" || typeof pair[1] !== "string") return null;
		const [name, text] = pair as Pair;
		if (!allowed.includes(name) || seen.has(name) || text.length > 500 || (name === "style" && UNSAFE_STYLE.test(text))) return null;
		seen.add(name);
	}
	return value as Pair[];
}

export const validSection = (value: unknown): string => (typeof value === "string" && SECTIONS.includes(value) ? value : "tbody");
export const validSpan = (value: unknown): number => (typeof value === "number" && Number.isInteger(value) && value >= 1 && value <= 1000 ? value : 1);
export const validAt = (value: unknown): number => (typeof value === "number" && Number.isInteger(value) && value >= -1 && value <= 20 ? value : -1);

export function validCaption(value: unknown): { title: string; number: string | null } | null {
	if (!value || typeof value !== "object") return null;
	const { title, number } = value as { title?: unknown; number?: unknown };
	const text = (part: unknown) => typeof part === "string" && part.length <= 1000 && !part.includes("\n");
	return text(title) && (number === null || text(number)) ? { title: title as string, number: number as string | null } : null;
}

// One or more `{: …}` lines, as the block IAL tokenizer reads them.
export function validIal(value: unknown): string | null {
	if (typeof value !== "string" || value.length > 2000) return null;
	return value.split("\n").every((line) => /^\{:(?:[^}"\n]|"[^"\n]*")*\}$/.test(line)) ? value : null;
}

const pairs = (el: Element, kind: Kind, extra: string[] = []): Pair[] | null =>
	validPairs(
		Array.from(el.attributes, (a): Pair => [a.name, a.value]),
		kind,
		extra,
	);

type Mark = { type: string; attrs?: { href?: string } };

function inline(node: Node, marks: Mark[]): JSONContent[] | null {
	const withMarks = (leaf: JSONContent): JSONContent[] => [marks.length ? { ...leaf, marks: [...marks] } : leaf];
	// The live view turns a newline typed or read back in a cell into a space, so such a table is not held.
	if (node.nodeType === 3) return (node as Text).data.includes("\n") ? null : (node as Text).data ? withMarks({ type: "text", text: (node as Text).data }) : [];
	if (node.nodeType !== 1) return null;
	const el = node as Element;
	const tag = el.tagName.toLowerCase();
	if (tag === "br") return el.attributes.length ? null : withMarks({ type: "hardBreak" });
	if (tag === "img") return withMarks({ type: "rawHtmlInline", attrs: { raw: el.outerHTML } });
	const mark: Mark | null =
		tag === "strong"
			? { type: "bold" }
			: tag === "em"
				? { type: "italic" }
				: tag === "span"
					? { type: "span" }
					: tag === "a"
						? { type: "link", attrs: { href: el.getAttribute("href") ?? "" } }
						: null;
	if (!mark || el.attributes.length !== (tag === "a" ? 1 : 0) || (tag === "a" && !el.hasAttribute("href"))) return null;
	const out: JSONContent[] = [];
	for (const child of Array.from(el.childNodes)) {
		const part = inline(child, [...marks, mark]);
		if (!part) return null;
		out.push(...part);
	}
	return out;
}

function content(el: Element): JSONContent[] | null {
	const out: JSONContent[] = [];
	for (const child of Array.from(el.childNodes)) {
		const part = inline(child, []);
		if (!part) return null;
		out.push(...part);
	}
	return out;
}

// The caption is a title and an optional number in a span: "Início de Carreira <span>tabela 2.1</span>".
function caption(el: Element): { title: string; number: string | null } | null {
	if (el.attributes.length) return null;
	const [first, second, ...rest] = Array.from(el.childNodes);
	const number = second as Element | undefined;
	if (rest.length || (first && first.nodeType !== 3)) return null;
	const title = (first as Text | undefined)?.data ?? "";
	// A text input holds no newline.
	if (title.includes("\n") || number?.textContent?.includes("\n")) return null;
	if (!number) return { title, number: null };
	if (number.nodeType !== 1 || number.tagName !== "SPAN" || number.attributes.length || number.children.length || !title.endsWith(" ")) return null;
	return { title: title.slice(0, -1), number: number.textContent ?? "" };
}

// Rows of a grid must all be as wide as the table, with spans that fit inside their section.
function fits(rows: { section: string; cells: { colspan: number; rowspan: number }[] }[]): boolean {
	const taken: number[] = [];
	let width = -1;
	for (const [i, row] of rows.entries()) {
		let col = 0;
		for (const { colspan, rowspan } of row.cells) {
			while ((taken[col] ?? 0) > i) col += 1;
			if (rowspan > 1 && (i + rowspan > rows.length || rows.slice(i, i + rowspan).some((spanned) => spanned.section !== row.section))) return false;
			for (let c = col; c < col + colspan; c += 1) {
				if ((taken[c] ?? 0) > i) return false;
				taken[c] = i + rowspan;
			}
			col += colspan;
		}
		while ((taken[col] ?? 0) > i) col += 1;
		if (width < 0) width = col;
		if (col !== width || col === 0) return false;
	}
	return rows.length > 0;
}

function toNode(table: Element): JSONContent | null {
	const tableHtml = pairs(table, "table");
	if (!tableHtml) return null;
	let title: { title: string; number: string | null } | null = null;
	const rows: JSONContent[] = [];
	for (const part of Array.from(table.children)) {
		const tag = part.tagName.toLowerCase();
		if (tag === "caption") {
			if (title || rows.length) return null;
			title = caption(part);
			if (!title) return null;
			continue;
		}
		if (!SECTIONS.includes(tag) || part.attributes.length) return null;
		for (const row of Array.from(part.children)) {
			const html = pairs(row, "tr");
			if (!html) return null;
			const cells: JSONContent[] = [];
			for (const cell of Array.from(row.children)) {
				const name = cell.tagName.toLowerCase();
				const kept = pairs(cell, "cell", ["colspan", "rowspan"]);
				const body = content(cell);
				if (!kept || !body || (name !== "td" && name !== "th")) return null;
				const span = (key: string) => (cell.hasAttribute(key) && /^[1-9]\d*$/.test(cell.getAttribute(key) ?? "") ? Number(cell.getAttribute(key)) : 1);
				const rest = kept.filter(([key]) => key !== "colspan" && key !== "rowspan");
				const [colspan, rowspan] = [span("colspan"), span("rowspan")];
				const written = kept.map(([key]) => key).join();
				const first = kept.findIndex(([key]) => key === "colspan" || key === "rowspan");
				const at =
					cellAttributes(rest, colspan, rowspan, -1)
						.map(([key]) => key)
						.join() === written
						? -1
						: Math.max(first, 0);
				cells.push({ type: name === "th" ? "tableHeader" : "tableCell", attrs: { colspan, rowspan, html: rest, at }, content: body });
			}
			rows.push({ type: "tableRow", attrs: { html, section: tag }, content: cells });
		}
	}
	const grid = rows.map((row) => ({
		section: row.attrs?.section,
		cells: (row.content ?? []).map((cell) => cell.attrs as { colspan: number; rowspan: number }),
	}));
	if (!fits(grid as Parameters<typeof fits>[0])) return null;
	return { type: "table", attrs: { html: tableHtml, caption: title, ial: null }, content: rows };
}

// A cell's attributes: its own in order, with the spans among them at `at`; -1 puts colspan first and rowspan last.
export function cellAttributes(html: Pair[], colspan: number, rowspan: number, at: number): Pair[] {
	const col: Pair[] = colspan > 1 ? [["colspan", String(colspan)]] : [];
	const row: Pair[] = rowspan > 1 ? [["rowspan", String(rowspan)]] : [];
	if (at < 0) return [...col, ...html, ...row];
	const list = [...html];
	list.splice(Math.min(at, list.length), 0, ...col, ...row);
	return list;
}

const attributes = (el: Element, list: Pair[]) => {
	for (const [key, value] of list) el.setAttribute(key, value);
};

const same = (a: Mark, b: Mark) => a.type === b.type && a.attrs?.href === b.attrs?.href;

// Marks that open together nest by how long they stay open, the longer outside; equal ones in MARK_ORDER.
function appendInline(doc: Document, cell: Element, nodes: JSONContent[]) {
	const open: { mark: Mark; el: Element }[] = [];
	const markers = nodes.map((node) => ((node.marks ?? []) as Mark[]).filter((mark) => MARK_ORDER.includes(mark.type)));
	const stays = (mark: Mark, from: number) => {
		let end = from;
		while (markers[end + 1]?.some((other) => same(other, mark))) end += 1;
		return end;
	};
	for (const [i, node] of nodes.entries()) {
		const marks = markers[i];
		let kept = 0;
		while (kept < open.length && marks.some((mark) => same(mark, open[kept].mark))) kept += 1;
		open.length = kept;
		const fresh = marks
			.filter((mark) => !open.some((o) => same(o.mark, mark)))
			.sort((a, b) => stays(b, i) - stays(a, i) || MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type));
		let parent = open.length ? open[open.length - 1].el : cell;
		for (const mark of fresh) {
			const el = doc.createElement({ link: "a", bold: "strong", italic: "em", span: "span" }[mark.type] as string);
			if (mark.type === "link") el.setAttribute("href", mark.attrs?.href ?? "");
			parent.append(el);
			open.push({ mark, el });
			parent = el;
		}
		if (node.type === "text") parent.append(doc.createTextNode(node.text ?? ""));
		else if (node.type === "hardBreak") parent.append(doc.createElement("br"));
		else {
			const holder = doc.createElement("div");
			holder.innerHTML = node.attrs?.raw ?? "";
			parent.append(...Array.from(holder.childNodes));
		}
	}
}

// The table's HTML in its one layout.
export function tableHtml(node: JSONContent): string | null {
	const doc = new DOMParser().parseFromString("<table></table>", "text/html");
	const table = doc.querySelector("table") as Element;
	attributes(table, validPairs(node.attrs?.html, "table") ?? []);
	const title = node.attrs?.caption as { title: string; number: string | null } | null;
	if (title) {
		const el = doc.createElement("caption");
		el.append(doc.createTextNode(title.title));
		if (title.number !== null) {
			const number = doc.createElement("span");
			number.textContent = title.number;
			el.append(doc.createTextNode(" "), number);
		}
		table.append(el);
	}
	let section: Element | null = null;
	let current = "";
	for (const row of node.content ?? []) {
		const name = validSection(row.attrs?.section);
		if (!section || name !== current) {
			section = doc.createElement(name);
			current = name;
			table.append(section);
		}
		const tr = doc.createElement("tr");
		attributes(tr, validPairs(row.attrs?.html, "tr") ?? []);
		section.append(tr);
		for (const cell of row.content ?? []) {
			const el = doc.createElement(cell.type === "tableHeader" ? "th" : "td");
			const { colspan, rowspan, html, at } = cell.attrs ?? {};
			attributes(el, cellAttributes(validPairs(html, "cell") ?? [], validSpan(colspan), validSpan(rowspan), validAt(at)));
			appendInline(doc, el, cell.content ?? []);
			tr.append(el);
		}
	}
	return layout(table);
}

// The node for a table's HTML in its one layout, or null when writing the node back would not give that HTML.
export function tableNode(html: string): JSONContent | null {
	const doc = new DOMParser().parseFromString(html, "text/html");
	const table = doc.querySelector("table");
	const node = table ? toNode(table) : null;
	return node && tableHtml(node) === html ? node : null;
}
